import { update } from "immupdate";
import { isBoolean } from "lodash";
import { useCallback, useEffect, useState } from "react";

export enum OrientationType {
  Portrait = "portrait",
  Landscape = "landscape",
  Undetermined = "undetermined",
}

export enum DeviceType {
  Mobile = "mobile",
  Tablet = "tablet",
  Desktop = "desktop",
  Undetermined = "undetermined",
  LargeDesktop = "large-desktop",
  ExtraLargeDesktop = "extra-large-desktop",
}

export interface MediaQueryProps {
  readonly isMobile: boolean;
  readonly isMobilePortrait: boolean;
  readonly isMobileLandscape: boolean;

  readonly isTablet: boolean;

  readonly isDesktop: boolean;
  readonly isLargeDesktop: boolean;
  readonly isExtraLargeDesktop: boolean;

  readonly device: DeviceType;
  readonly orientation: OrientationType;
}

const detectMobile = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return toMatch.some(toMatchItem => {
    return navigator.userAgent.match(toMatchItem);
  });
};

export const useMediaQuery = (): MediaQueryProps => {
  const [state, setState] = useState<MediaQueryProps>({
    isDesktop: false,
    isLargeDesktop: false,
    isExtraLargeDesktop: false,

    isTablet: false,

    isMobile: detectMobile(),
    isMobilePortrait: false,
    isMobileLandscape: false,

    device: DeviceType.Undetermined,
    orientation: OrientationType.Undetermined,
  });

  //====================== Orientation ==========================
  const changeOrientationHandler = useCallback((mql: MediaQueryList) => {
    setState(x =>
      update(x, {
        orientation: isBoolean(mql?.matches)
          ? mql.matches
            ? OrientationType.Portrait
            : OrientationType.Landscape
          : OrientationType.Undetermined,
      }),
    );
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)");

    if (mql) {
      changeOrientationHandler(mql);
    }

    if (mql?.addListener) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      mql.addListener(changeOrientationHandler);
    }

    return () => {
      if (mql?.removeListener) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        mql.removeListener(changeOrientationHandler);
      }
    };
  }, [changeOrientationHandler]);
  //====================== Orientation ==========================

  //========================= Device ============================
  const changeMobileDeviceHandler = useCallback((mql: MediaQueryList) => {
    if (mql.matches) {
      setState(x =>
        update(x, {
          device: DeviceType.Mobile,

          isTablet: false,
          isDesktop: false,
          isLargeDesktop: false,
          isExtraLargeDesktop: false,

          isMobile: detectMobile(),
          isMobilePortrait: x.orientation === OrientationType.Portrait,
          isMobileLandscape: x.orientation === OrientationType.Landscape,
        }),
      );
    }
  }, []);
  const changeTabletDeviceHandler = useCallback((mql: MediaQueryList) => {
    if (mql.matches) {
      setState(x =>
        update(x, {
          isTablet: true,

          isDesktop: false,
          isLargeDesktop: false,
          isExtraLargeDesktop: false,

          isMobile: detectMobile(),
          isMobilePortrait: false,
          isMobileLandscape: false,

          device: DeviceType.Tablet,
        }),
      );
    }
  }, []);
  const changeDesktopDeviceHandler = useCallback((mql: MediaQueryList) => {
    if (mql.matches) {
      setState(x =>
        update(x, {
          isDesktop: true,

          isTablet: false,
          isLargeDesktop: false,
          isExtraLargeDesktop: false,

          isMobile: detectMobile(),
          isMobilePortrait: false,
          isMobileLandscape: false,

          device: DeviceType.Desktop,
        }),
      );
    }
  }, []);
  const changeLargeDesktopDeviceHandler = useCallback((mql: MediaQueryList) => {
    if (mql.matches) {
      setState(x =>
        update(x, {
          isLargeDesktop: true,

          isTablet: false,
          isDesktop: false,
          isExtraLargeDesktop: false,

          isMobile: detectMobile(),
          isMobilePortrait: false,
          isMobileLandscape: false,

          device: DeviceType.LargeDesktop,
        }),
      );
    }
  }, []);
  const changeExtraLargeDesktopDeviceHandler = useCallback((mql: MediaQueryList) => {
    if (mql.matches) {
      setState(x =>
        update(x, {
          isExtraLargeDesktop: true,

          isTablet: false,
          isDesktop: false,
          isLargeDesktop: false,

          isMobile: detectMobile(),
          isMobilePortrait: false,
          isMobileLandscape: false,

          device: DeviceType.ExtraLargeDesktop,
        }),
      );
    }
  }, []);

  useEffect(() => {
    const mobileMql = window.matchMedia(
      "(min-width: 320px) and (max-width: 480px), (min-width: 481px) and (max-width: 767px)",
    );

    if (mobileMql) {
      changeMobileDeviceHandler(mobileMql);
    }

    if (mobileMql?.addListener) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      mobileMql.addListener(changeMobileDeviceHandler);
    }

    return () => {
      if (mobileMql?.removeListener) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        mobileMql.removeListener(changeMobileDeviceHandler);
      }
    };
  }, [changeMobileDeviceHandler]);

  useEffect(() => {
    const tabletMql = window.matchMedia("(min-width: 768px) and (max-width: 1024px)");

    if (tabletMql) {
      changeTabletDeviceHandler(tabletMql);
    }

    if (tabletMql?.addListener) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      tabletMql.addListener(changeTabletDeviceHandler);
    }

    return () => {
      if (tabletMql?.removeListener) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        tabletMql.removeListener(changeTabletDeviceHandler);
      }
    };
  }, [changeTabletDeviceHandler]);

  useEffect(() => {
    const desktopMql = window.matchMedia("(min-width: 1025px) and (max-width: 1200px)");

    if (desktopMql) {
      changeDesktopDeviceHandler(desktopMql);
    }

    if (desktopMql?.addListener) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      desktopMql.addListener(changeDesktopDeviceHandler);
    }

    return () => {
      if (desktopMql?.removeListener) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        desktopMql.removeListener(changeDesktopDeviceHandler);
      }
    };
  }, [changeDesktopDeviceHandler]);

  useEffect(() => {
    const largeDesktopMql = window.matchMedia("(min-width: 1201px) and (max-width: 1600px)");

    if (largeDesktopMql) {
      changeLargeDesktopDeviceHandler(largeDesktopMql);
    }

    if (largeDesktopMql?.addListener) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      largeDesktopMql.addListener(changeLargeDesktopDeviceHandler);
    }

    return () => {
      if (largeDesktopMql?.removeListener) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        largeDesktopMql.removeListener(changeLargeDesktopDeviceHandler);
      }
    };
  }, [changeLargeDesktopDeviceHandler]);

  useEffect(() => {
    const extraLargeDesktopMql = window.matchMedia("(min-width: 1601px)");

    if (extraLargeDesktopMql) {
      changeExtraLargeDesktopDeviceHandler(extraLargeDesktopMql);
    }

    if (extraLargeDesktopMql?.addListener) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      extraLargeDesktopMql.addListener(changeExtraLargeDesktopDeviceHandler);
    }

    return () => {
      if (extraLargeDesktopMql?.removeListener) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        extraLargeDesktopMql.removeListener(changeExtraLargeDesktopDeviceHandler);
      }
    };
  }, [changeExtraLargeDesktopDeviceHandler]);
  //========================= Device ============================

  return state;
};
